import React from 'react';
import { motion } from 'framer-motion';
import { POI } from '../../types';
import { Trees, UtensilsCrossed, Dumbbell } from 'lucide-react';
interface POIMarkerProps {
  poi: POI;
  isSelected: boolean;
  onSelect: (poi: POI) => void;
  delay?: number;
}
// Map category to icon component
const getCategoryIcon = (category: POI['category']) => {
  switch (category) {
    case 'Nature':
      return Trees;
    case 'Dining':
      return UtensilsCrossed;
    case 'Fitness':
      return Dumbbell;
    default:
      return Trees;
  }
};
// Map category to color
const getCategoryColor = (category: POI['category']) => {
  switch (category) {
    case 'Nature':
      return 'text-green-400';
    case 'Dining':
      return 'text-orange-400';
    case 'Fitness':
      return 'text-blue-400';
    default:
      return 'text-green-400';
  }
};
export function POIMarker({
  poi,
  isSelected,
  onSelect,
  delay = 0
}: POIMarkerProps) {
  const Icon = getCategoryIcon(poi.category);
  const colorClass = getCategoryColor(poi.category);
  return <motion.button initial={{
    opacity: 0,
    scale: 0,
    y: 20
  }} animate={{
    opacity: 1,
    scale: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0
  }} transition={{
    delay,
    type: 'spring',
    stiffness: 300,
    damping: 20
  }} onClick={e => {
    e.stopPropagation();
    onSelect(poi);
  }} className="absolute transform -translate-x-1/2 -translate-y-1/2 group z-20" style={{
    left: `${poi.coordinates.x}%`,
    top: `${poi.coordinates.y}%`
  }} data-id="element-821">
      {/* Pulse Effect - restored with slower repeat */}
      <motion.div className={`absolute inset-0 rounded-full ${isSelected ? 'bg-cyan-500' : 'bg-white'}`} animate={{
      scale: [1, 2],
      opacity: [0.75, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      repeatDelay: 1,
      ease: 'easeOut'
    }} data-id="element-822" />

      {/* Marker Icon */}
      <div className={`
        relative w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 transition-all duration-300
        ${isSelected ? 'bg-cyan-500 border-white scale-125' : 'bg-slate-900 border-white/50 hover:bg-cyan-500 hover:border-white hover:scale-110'}
      `} data-id="element-823">
        <Icon className={`w-5 h-5 transition-colors ${isSelected ? 'text-white' : `${colorClass} group-hover:text-white`}`} data-id="element-824" />
      </div>

      {/* Label (Visible on hover or selection) */}
      <div className={`
        absolute top-12 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider shadow-xl transition-all duration-300
        ${isSelected ? 'bg-cyan-500 text-white opacity-100 translate-y-0' : 'bg-slate-900/90 text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'}
      `} data-id="element-825">
        {poi.name}
      </div>
    </motion.button>;
}